import { ResultsSheet } from '@/components/CarePage/ResultsSheet'
import { Row, StatusPill } from '@/components/Rows'
import type { GlyphName } from '@/components/svgs/Glyph'
import {
	RECORDS_COMING,
	type VisitRecord,
	canOpenReport,
	resultsSummary,
	rowStateOf,
	visitSubtitle,
} from '@/features/care/visits'
import { Header } from '@/ui/core/header'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { useState } from 'react'
import { View } from 'react-native'

/**
 * The three parts of a visit the app cannot reach yet.
 *
 * Named and listed rather than left out, because a visit that shows only a score reads as a visit
 * where only a score was taken. A member who was photographed and measured for forty minutes
 * should see the shape of what was collected, even while the app can open one part of it.
 */
const COMING: { title: string; glyph: GlyphName }[] = [
	// Two marks drawn for this list, because the borrowed ones collided: `photo` is a holiday snap
	// and `camera` the instrument beside it, which is two cameras in one list, and a molar at
	// eighteen points is a blob. `form` — a sheet with writing on it — is what a note is, so it
	// stays.
	{ title: 'X-rays', glyph: 'zap' },
	{ title: 'Scans', glyph: 'sweep' },
	{ title: 'Notes', glyph: 'form' },
]

/** What the results row says about itself, in its three states. */
const resultsLine = (visit: VisitRecord) =>
	visit.results
		? resultsSummary(visit.results)
		: visit.resultsPending
		  ? 'Being reviewed by your clinician'
		  : 'This visit did not produce a score'

/**
 * One visit's record: its results, and the three groups that are not here yet.
 *
 * The row it came from used to grow in place and hold a single white row inside a blush one — two
 * containers deep to show one line — and then it opened a sheet. A visit is a document with parts
 * and it is the destination for everything a member wants to read, so it is a page: four rows in
 * the same shape as the list they came from, results first.
 *
 * The three empty ones are dimmed under a Coming Soon pill rather than omitted. The page is
 * honestly three-quarters empty and says so once, in the same words the list uses, from the same
 * constant — so the day the records land, the promise is deleted in one place.
 */
export const VisitDetail = ({ visit }: { visit: VisitRecord }) => {
	const [results, setResults] = useState(false)

	return (
		<VStack gap={20}>
			<VStack gap={6}>
				<Header variant="h2">{visit.name}</Header>
				<Text size="body0" tone="soft">
					{visitSubtitle(visit)}
				</Text>
			</VStack>
			{/* Results has a card to itself.
			    In one card with the three that are not here yet, it was the first row of a list
			    whose other three rows are all "not yet" — so the one thing a member can actually
			    open read as the top of a queue of things they cannot. Its own card makes it the
			    page's subject and leaves the others as what they are: a list of what is coming. */}
			<View className="rounded-note bg-blush px-4">
				<Row
					// The score's own arc, so the row wears the shape of the thing it opens. It was
					// `sparkle`, which is also the score card's mark, the visit disc's and the
					// sheet title's — one glyph doing four jobs says nothing about any of them.
					glyph="gauge"
					title="Results"
					line={resultsLine(visit)}
					wrap
					last
					// Openable only with the whole report behind it. A score whose findings the app
					// cannot reach still states itself here; it just has nothing to open.
					onPress={canOpenReport(visit) ? () => setResults(true) : undefined}
					// On the title's line rather than at the row's end, so "Being reviewed by your
					// clinician" keeps the full width beneath it.
					tag={
						rowStateOf(visit) === 'pending' ? (
							<StatusPill status="wait" label="Processing" onGround />
						) : undefined
					}
					tagOnTitle
				/>
			</View>
			<View className="rounded-note bg-blush px-4">
				{COMING.map(({ title, glyph }, index) => (
					// Dimmed as a block: the row draws its own hairline, and a row at full strength
					// with a Coming Soon pill on it reads as something a member failed to open.
					<View key={title} style={{ opacity: 0.55 }}>
						<Row
							glyph={glyph}
							title={title}
							tag={<StatusPill status="wait" label="Coming Soon" onGround />}
							tagOnTitle
							last={index === COMING.length - 1}
						/>
					</View>
				))}
			</View>
			{/* The same sentence as on Care, from the same constant, at the foot of the page it is
		    explaining. */}
			<Text size="body2" tone="soft" className="text-center">
				{RECORDS_COMING}
			</Text>
			<ResultsSheet visit={visit} open={results} onClose={() => setResults(false)} />
		</VStack>
	)
}
